import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import { listMobilizers } from '@/features/mobilizers/api'
import { roleLabel } from '@/features/mobilizers/vocab'
import type { SurveyResponseDto, SurveyStratumDto } from '@/shared/api/types'
import { assign } from './api'
import { DateTimePicker } from '@/components/erp/date-picker'

/** `datetime-local` speaks local wall-clock with no zone; `dueAt` is an Instant. */
function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * Putting one interviewer on one poll.
 *
 * ## The picker is the roster's, not a second copy of it
 *
 * There is no standalone mobilizer picker component under `features/mobilizers`
 * — `SupervisorPicker` is the only picker there and it is bound to the hierarchy
 * move it performs. So this reuses the call that one uses, `listMobilizers`,
 * through the same `AsyncMultiSelect`. Forty thousand field staff cannot be
 * shipped into a `<select>`, and a second list endpoint would be a second
 * definition of who is active.
 *
 * Filtered to `ACTIVE` on the way out: a terminated mobilizer is a person who
 * still has field history and must keep it, and offering them work is a
 * different thing from remembering they did some.
 *
 * ## Where — defaulted, narrowable, server-enforced
 *
 * `scopeUnitId` is optional on `AssignmentRequest` and the poll's own scope is
 * the sensible default: most assignments cover the whole poll. Narrowing is how
 * you split a constituency between four interviewers, and it is the ONLY reason
 * to touch the control. The unit must sit inside the poll's scope — the server
 * checks it, and this dialog does not pre-check, because a client that guesses
 * the containment rule and guesses wrong hides a legal assignment behind a
 * refusal nobody can appeal.
 *
 * ## Stratum — offered only when the poll has strata
 *
 * Not hidden on a maybe: a poll with no strata has nothing to offer here, and an
 * empty dropdown is worse than no dropdown. When there ARE strata, saying which
 * one an assignment fields is what lets the design weight be computed per
 * stratum (`N_h / n_h`) instead of pooled across the whole sample.
 */
export function AssignDialog({ open, poll, strata, onClose, onSaved }: {
  open: boolean
  poll: SurveyResponseDto
  /** From `listStrata`. Empty means this poll is unstratified. */
  strata: SurveyStratumDto[]
  onClose: () => void
  onSaved: () => void
}) {
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [unit, setUnit] = useState<CascadeValue>({ path: [] })
  const [stratumId, setStratumId] = useState('')
  const [targetCount, setTargetCount] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPicked([])
    setUnit({ path: [] })
    setStratumId('')
    setTargetCount('')
    setDueAt('')
    setError(null)
  }, [open, poll.id])

  const mobilizerId = picked[0]?.value ?? ''
  const chosenStratum = strata.find((s) => s.id === stratumId) ?? null
  const fieldworkOver = poll.statusCode === 'CLOSED' || poll.statusCode === 'ARCHIVED'

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await assign(poll.id, {
        mobilizerId,
        // Falls back to the poll's scope rather than to null: an assignment with
        // no unit is one nothing can scope-check, and the default is what the
        // dialog says it is doing.
        scopeUnitId: unit.unitId ?? poll.scopeUnitId,
        stratumId: stratumId === '' ? null : stratumId,
        // '' is "no target" and 0 is "a target of nobody". Number('') is 0, which
        // turns the first silently into the second.
        targetCount: targetCount.trim() === '' ? null : Number(targetCount),
        dueAt: fromLocalInput(dueAt),
      })
      onSaved()
    } catch (e) {
      // The two refusals this dialog exists to surface, both server-composed:
      //  · `survey/not-ready` — the poll is CLOSED or ARCHIVED and is not
      //    collecting, so there is nothing for an interviewer to field.
      //  · the open-assignment cap, `survey.fieldwork.max-open-assignments`
      //    (default 3, settable 1–20). It is a per-interviewer cap on how many
      //    polls somebody can hold at once, and the refusal names the number —
      //    which is why this does not count assignments client-side and guess.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title={`Assign an interviewer to ${poll.ref}`}
      description="One person, one area, one target. The attempts they record are what the response rates are computed from."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Offered even where it will be refused — including on a CLOSED poll and
            for an interviewer already at the cap. Both refusals arrive as a
            sentence naming the actual limit; a button that greys itself out
            cannot say which of the two it was.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {fieldworkOver && (
          <Note tone="bad">
            {poll.ref} is {poll.statusCode}. Fieldwork is over, so the server will refuse this
            assignment — the control is still here because a missing button and a refused one
            are indistinguishable from the outside, and the refusal says which poll and which
            status.
          </Note>
        )}

        <Field label="Interviewer" required
          hint="Active field staff only. A terminated mobilizer keeps their history and is not offered new work.">
          <AsyncMultiSelect
            label="Interviewer"
            selected={picked}
            // Single choice through a multi-select: `slice(-1)` keeps the last
            // click, which is what a person expects from a picker that looks like
            // a dropdown. `AssignmentRequest.mobilizerId` is one UUID.
            onChange={(next) => setPicked(next.slice(-1))}
            placeholder="Search by name"
            emptyLabel="Nobody chosen"
            search={async (term) => {
              const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
              return page.content.map((m) => ({
                value: m.id,
                label: m.fullName,
                sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
              }))
            }}
          />
        </Field>

        <Field label="Where"
          hint="Optional. Leave it alone to assign the poll's whole scope; narrow it to split that scope between several interviewers.">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2 text-erp-sm">
              <span className="text-muted-foreground">Defaults to the poll's scope:</span>
              <span>{poll.scopeUnitName ?? poll.scopeUnitId ?? 'no scope on this poll'}</span>
            </div>
            <UnitCascade value={unit} onChange={setUnit} />
          </div>
        </Field>

        {unit.unitId && (
          <Note tone="info">
            The unit has to sit inside {poll.scopeUnitName ?? "the poll's scope"}. The server
            checks that and refuses with a sentence naming both units — this dialog does not
            pre-check it, because a client that guesses the containment rule wrongly hides
            assignments that were legal all along.
          </Note>
        )}

        {strata.length > 0 && (
          <Field label="Stratum"
            hint="Optional, and the reason to use it is weighting, not tidiness.">
            <select className="erp-input h-9 w-full" value={stratumId}
              onChange={(e) => setStratumId(e.target.value)}>
              <option value="">Not tied to a stratum</option>
              {strata.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {strata.length > 0 && (
          <Note tone="info">
            {chosenStratum
              ? `Interviews collected under this assignment are attributed to ${chosenStratum.code}, `
                + 'so the design weight for them is that stratum\'s own population over its own '
                + 'achieved n. Left untied, they are weighted against the pooled sample instead — '
                + 'which is the same weight for everybody and undoes the point of stratifying.'
              : 'This poll declares ' + strata.length + ' strata. Tying the assignment to one is '
                + 'what lets the design weight be computed per stratum — population over achieved '
                + 'n, for that slice. Left untied the interviews are pooled, and every respondent '
                + 'gets the same weight regardless of which slice they came from.'}
          </Note>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Target"
            hint="How many interviews this assignment is meant to yield. Leave empty for no target.">
            <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={0}
              value={targetCount} onChange={(e) => setTargetCount(e.target.value)}
              placeholder="120" />
          </Field>
          <Field label="Due"
            hint="Optional. An assignment past its due date goes EXPIRED rather than disappearing.">
            <DateTimePicker value={dueAt} onChange={setDueAt} className="h-9 w-full" />
          </Field>
        </div>

        <p className="text-erp-xs text-muted-foreground">
          One person can only hold so many open assignments at once —
          <span className="font-mono"> survey.fieldwork.max-open-assignments</span>, three by
          default and settable between one and twenty. The server counts the open ones and
          refuses over the line with the figure it used, so the answer here is that refusal
          rather than a number this dialog guessed.
        </p>
      </div>
    </Modal>
  )
}
